import { useFreeGiftInteraction } from '@/components/blocks/FreeGiftV2/context/FreeGiftInteractionContext';
import { useFreeGiftPromotion } from '@/components/blocks/FreeGiftV2/context/FreeGiftPromotionContext';
import { PromotionTitleSx } from '@/components/blocks/FreeGiftV2/styles/freeGift';
import { getRewardsQuantities } from '@/components/blocks/FreeGiftV2/utils';
import { useLocalization } from '@/data/Localization';
import { Chip, Stack, Typography } from '@mui/material';

export const PromotionTitle = () => {
	const {
		rewardsQty,
		isPurchaseCombinable,
		othersQty,
		showUpToLabel,
		reward,
		purchase,
		currentPartNumber,
		purchaseQty,
		promotionGroups,
	} = useFreeGiftPromotion();
	const { isRewardView } = useFreeGiftInteraction();
	const labels = useLocalization('FreeGift');
	const labelsReward = useLocalization('FreeReward');

	const hasMultiplePromotions = promotionGroups.length > 1;
	const { othersQty: extraGiftTarget } = getRewardsQuantities(reward, currentPartNumber);
	const requiredBuyQty = purchaseQty + othersQty;

	const noCombinableGroupQuantities = (purchase?.purchases ?? []).map((group) => group.quantity);
	const hasVaryingGroupQuantities =
		!isPurchaseCombinable &&
		noCombinableGroupQuantities.length > 1 &&
		new Set(noCombinableGroupQuantities).size > 1;
	const maxRequiredBuyQty =
		noCombinableGroupQuantities.length > 0
			? Math.max(...noCombinableGroupQuantities)
			: requiredBuyQty;

	const getTitleText = () => {
		if (isRewardView) {
			if (hasMultiplePromotions) {
				return labelsReward.GetThisGift.t();
			}

			return extraGiftTarget === 0
				? labelsReward.GetThisGift.t()
				: labelsReward.GetThisAndOtherGifts.t();
		}

		if (hasMultiplePromotions) {
			return labels.Take1Gift.t();
		}

		if (showUpToLabel) {
			return labels.TakeNGiftUpTo.t({ n: rewardsQty });
		}

		return rewardsQty === 1 ? labels.Take1Gift.t() : labels.TakeNGifts.t({ n: rewardsQty });
	};

	const getSubtitleText = () => {
		if (isRewardView) {
			if (hasMultiplePromotions) {
				return labelsReward.FreeBuyEligibleItems.t();
			}

			return hasVaryingGroupQuantities
				? labelsReward.BuyUpToEligibleItems.t({ n: maxRequiredBuyQty })
				: labelsReward.BuyEligibleItems.t({ n: requiredBuyQty });
		}

		if (!isPurchaseCombinable) {
			return rewardsQty === 1
				? labels.FreeBuyingThisProduct.t()
				: labels.FreeBuyingTheseProducts.t();
		}

		return othersQty > 0
			? labels.FreeBuyingTheseNProducts.t({ n: othersQty })
			: labels.FreeBuyingThisProduct.t();
	};

	const titleText = getTitleText();
	const subtitleText = getSubtitleText();

	const chipText = isRewardView
		? labelsReward.FreeWithEligibleItems.t()
		: labels.FreeProductWithPurchase.t();

	return (
		<Stack display="flex" gap="10px">
			<Chip label={chipText} size="small" sx={PromotionTitleSx.chip} />
			<Typography variant="headingSegment" sx={PromotionTitleSx.titleText}>
				{titleText}
			</Typography>
			<Typography variant="bodySubText" color="grey.300">
				{subtitleText}
			</Typography>
		</Stack>
	);
};
